import { Box } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { ReactNode } from 'react';

import {
  COLOR_PICKER_WIDTH,
  DEFAULT_SATURATION_BORDER_COLOR,
  SATURATION_BORDER,
  SATURATION_BORDER_RADIUS,
  SATURATION_BORDER_THICKNESS,
  SLIDER_BORDER_RADIUS,
  SLIDER_HEIGHT,
} from './ColorPicker.config';

interface HexAlphaColorPickerWrapperProps {
  showOpacityOption?: boolean;
  children?: ReactNode;
  className?: string;

  containerWidth?: string;

  // Saturation box properties
  saturationHeight?: string;
  saturationBorderColor?: string;
}

// Container to use with emotion styled so that we can pass in the saturationBorderColor prop
const Container = ({ className, children }: HexAlphaColorPickerWrapperProps) => (
  <Box className={className}>{children}</Box>
);

/**
 * Generate CSS for a tooltip - used for hue/opacity pointers
 *
 * @param className Class name of the element to apply the tooltip to
 * @param label Tooltip text
 * @returns CSS string
 */
const TOOLTIP_CSS = (className: string, label: string) => `
  ${className}:before {
    display: none;

    content: '${label}';
    width: fit-content;
    height: fit-content;
    font-size: var(--vg-fontSizes-sm);
    border-radius: var(--vg-radii-sm);

    padding: var(--vg-space-0-5) var(--vg-space-2);

    background-color: black;
    color: white;

    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-top: 8px;

    z-index: var(--vg-zIndices-tooltip);
  }

  // Display tooltip on hover
  ${className}:hover:before {
    display: block;
  }

  // Hide tooltip when being dragged
  ${className}:active:before {
    display: none;
  }
`;

/**
 * Wrapper component for react-colorful's HexAlphaColorPicker component
 * Styles the individual components of the picker:
 * - Hue Slider
 * - Opacity Slider
 * - Saturation Picker
 */
export const HexAlphaColorPickerWrapper = styled(Container)`
  .react-colorful__pointer {
    width: var(--vg-sizes-5);
    height: var(--vg-sizes-5);

    border: 1.5px solid var(--vg-colors-gray-50);
    box-shadow: 0px 0px 2px var(--vg-colors-blackAlpha-600);
  }

  .react-colorful__pointer:hover {
    cursor: pointer;
  }

  .react-colorful__interactive:focus .react-colorful__pointer {
    box-shadow: var(--vg-shadows-outline);
  }

  .react-colorful {
    gap: ${(props) => (props.showOpacityOption ? '10px' : '14px')};
    margin-left: 0;
    height: fit-content;
    width: ${(props) => props.containerWidth || `${COLOR_PICKER_WIDTH}px`};
  }

  .react-colorful__saturation {
    order: 3;
    height: ${(props) => props.saturationHeight || '96px'};
    flex-grow: 0;
    margin-top: ${(props) => (props.showOpacityOption ? '2px' : '14px')};
    border-radius: 0px;
    border-top: ${SATURATION_BORDER};
    border-bottom: ${SATURATION_BORDER};
    border-right: ${SATURATION_BORDER};
    border-top-right-radius: ${SATURATION_BORDER_RADIUS};
    border-bottom-right-radius: ${SATURATION_BORDER_RADIUS};
    border-color: ${DEFAULT_SATURATION_BORDER_COLOR};
    box-shadow: ${(props) =>
      props.saturationBorderColor
        ? `
      ${SATURATION_BORDER_THICKNESS / 2}px 0 0 ${SATURATION_BORDER_THICKNESS / 2}px ${
            props.saturationBorderColor
          }, // right border
      0 ${SATURATION_BORDER_THICKNESS}px 0 ${props.saturationBorderColor}, // top border
      0 -${SATURATION_BORDER_THICKNESS}px 0 ${props.saturationBorderColor} // bottom border
    `
        : ''};
  }

  // HUE SLIDER
  .react-colorful__hue {
    width: 105%;
    left: -5%;
    order: 1;
    height: ${SLIDER_HEIGHT};
    border-radius: ${SLIDER_BORDER_RADIUS};
  }

  // OPACITY SLIDER
  .react-colorful__alpha {
    width: 105%;
    left: -5%;
    order: 2;
    top: 0;
    height: ${SLIDER_HEIGHT};
    border-radius: ${SLIDER_BORDER_RADIUS};
    display: ${(props) => (props.showOpacityOption ? '' : 'none')}};
  }

  // POINTER STYLING
  .react-colorful__hue-pointer,
  .react-colorful__alpha-pointer {
    height: 16px;
    width: 16px;
  }

  .react-colorful__pointer.react-colorful__saturation-pointer {
    height: 18px;
    width: 18px;
    z-index: 0;
  }

  // TOOLTIPS FOR POINTERS
  ${TOOLTIP_CSS('.react-colorful__hue-pointer', 'Hue')}
  ${TOOLTIP_CSS('.react-colorful__alpha-pointer', 'Opacity')}
`;
